import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { KeyRound, Lock } from 'lucide-react';
import { api } from '../../lib/api';
import { Notice, errorText } from '../../lib/crm';
import { PageHeader } from '../../components/AppShell';
import { Column, DataTable, FilterBar, SearchInput } from '../../components/DataTable';
import { Button, Modal, StatusBadge, useToast } from '../../components/ui';

interface Person {
  id: string; fullName: string; employeeCode: string; roleKey: string; roleName: string;
  viaOverride: boolean; hasPricingAccess: boolean; locked: boolean;
}

export function PricingAccessPage() {
  const qc = useQueryClient();
  const toast = useToast();
  const [search, setSearch] = useState('');
  const [revoking, setRevoking] = useState<Person | null>(null);

  const q = useQuery({ queryKey: ['crm', 'pricing-access'], queryFn: () => api.get<{ data: Person[] }>('/crm/pricing-access') });
  const all = q.data?.data ?? [];
  const holders = all.filter((p) => p.hasPricingAccess);
  const rows = all.filter((p) => !search || `${p.fullName} ${p.employeeCode} ${p.roleName}`.toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => Number(b.hasPricingAccess) - Number(a.hasPricingAccess) || a.fullName.localeCompare(b.fullName));

  const change = useMutation({
    mutationFn: (v: { p: Person; grant: boolean }) => api.post(`/crm/pricing-access/${v.p.id}`, { grant: v.grant }),
    onSuccess: (_d, v) => {
      void qc.invalidateQueries({ queryKey: ['crm', 'pricing-access'] });
      toast.success(v.grant ? `Pricing access granted to ${v.p.fullName}` : `Pricing access removed from ${v.p.fullName}`,
        'It applies from their next request; no sign-out is needed.');
      setRevoking(null);
    },
    onError: (e) => toast.error('Could not change access', errorText(e)),
  });

  const columns: Array<Column<Person>> = [
    { key: 'name', header: 'Employee', render: (p) => (
      <div><p className="font-medium text-ink-900">{p.fullName}</p><p className="tabular text-xs text-ink-500">{p.employeeCode}</p></div>) },
    { key: 'role', header: 'Role', hideBelow: 'md', render: (p) => p.roleName },
    { key: 'access', header: 'Pricing access', render: (p) => p.hasPricingAccess
      ? <StatusBadge tone="success">{p.locked ? 'Always (Super Admin)' : p.viaOverride ? 'Granted to this person' : 'Through their role'}</StatusBadge>
      : <StatusBadge tone="neutral">None</StatusBadge> },
    { key: 'action', header: '', numeric: true, render: (p) => p.locked ? (
      <span className="inline-flex items-center gap-1 text-xs text-ink-500"><Lock className="h-3.5 w-3.5" aria-hidden />Locked</span>
    ) : p.hasPricingAccess ? (
      <Button size="sm" onClick={() => setRevoking(p)}>Remove access</Button>
    ) : (
      <Button size="sm" variant="primary" loading={change.isPending && change.variables?.p.id === p.id}
        onClick={() => change.mutate({ p, grant: true })}>Grant access</Button>) },
  ];

  return (
    <>
      <PageHeader title="Pricing access"
        description="Decide who prices BOQs and sees confidential cost and margin figures." />
      <div className="space-y-4 p-4 sm:p-6">
        <Notice>
          Pricing access has three parts: <strong>pricing</strong> (set costs, charges and discounts),
          <strong> approval</strong> (release the price to Sales) and <strong>cost and margin visibility</strong>.
          Everyone else sees selling prices only. Discounts above 10% or margins under 8% still need Management sign-off.
        </Notice>
        <p className="text-sm text-ink-600" aria-live="polite">
          {q.isLoading ? 'Loading…' : holders.length
            ? <>Currently held by <strong>{holders.map((h) => h.fullName).join(', ')}</strong>.</>
            : 'Nobody holds pricing access.'}
        </p>
        <DataTable mobile="cards" columns={columns} rows={rows} rowKey={(p) => p.id} loading={q.isLoading} error={q.error} onRetry={() => q.refetch()}
          empty={{ icon: <KeyRound className="h-5 w-5" />, title: 'No employees match' }}
          toolbar={<FilterBar><SearchInput value={search} onChange={setSearch} placeholder="Name, code or role" /></FilterBar>} />
      </div>

      <Modal open={!!revoking} onClose={() => setRevoking(null)} size="sm" title="Remove pricing access?"
        description={revoking ? `${revoking.fullName} will no longer be able to price BOQs, approve prices or see cost and margin.` : undefined}
        footer={<><Button onClick={() => setRevoking(null)}>Keep access</Button>
          <Button variant="danger" loading={change.isPending} onClick={() => revoking && change.mutate({ p: revoking, grant: false })}>Remove access</Button></>}>
        <p className="text-sm text-ink-700">
          Work already priced stays as it is. Any price waiting for this person will need someone else with access to take it forward.
        </p>
      </Modal>
    </>
  );
}
